import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

interface OpticsState {
  waveguideAngle: number;
  doeRedirection: number;
  lensAdjustment: number;
  focalLength: number;
  propagationState: string;
  timestamp: number;
}

interface ProjectionStatus {
  ready: boolean;
  auraValidated: boolean;
  opticsAligned: boolean;
  projectionActive: boolean;
}

export function OpticsWaveguideDashboard() {
  const [opticsState, setOpticsState] = useState<OpticsState>({
    waveguideAngle: 40,
    doeRedirection: 0,
    lensAdjustment: 0.35,
    focalLength: 0,
    propagationState: 'idle',
    timestamp: Date.now()
  });

  const [projectionStatus, setProjectionStatus] = useState<ProjectionStatus>({
    ready: false,
    auraValidated: false,
    opticsAligned: false,
    projectionActive: false
  });

  const [isRunning, setIsRunning] = useState(false);

  const runOpticsDiagnostics = () => {
    setIsRunning(true);
    setOpticsState(prev => ({ ...prev, propagationState: 'propagating' }));

    // Simulate optics calculations
    setTimeout(() => {
      const inputAngle = opticsState.waveguideAngle;
      const doeAngle = inputAngle * 0.85 + (Math.random() - 0.5) * 2;
      const focalLength = 25.0 + opticsState.lensAdjustment * 15.0;
      
      setOpticsState(prev => ({
        ...prev,
        doeRedirection: doeAngle,
        focalLength,
        propagationState: 'stable',
        timestamp: Date.now()
      }));

      // Check alignment
      const aligned = Math.abs(doeAngle - inputAngle) < 5.0 && focalLength > 20.0;
      
      setProjectionStatus(prev => ({
        ...prev,
        opticsAligned: aligned,
        ready: aligned && prev.auraValidated
      }));

      setIsRunning(false);
    }, 1500);
  };

  const adjustWaveguide = (angle: number) => {
    setOpticsState(prev => ({ ...prev, waveguideAngle: angle }));
  };

  const adjustLens = (adjustment: number) => {
    setOpticsState(prev => ({ ...prev, lensAdjustment: adjustment }));
  };

  const activateProjection = () => {
    if (projectionStatus.ready) {
      setProjectionStatus(prev => ({ ...prev, projectionActive: !prev.projectionActive }));
    }
  };

  // Simulate aura validation updates
  useEffect(() => {
    const interval = setInterval(() => {
      setProjectionStatus(prev => ({
        ...prev,
        auraValidated: Math.random() > 0.3 // 70% chance of validation
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: boolean) => {
    return status ? 'bg-green-500' : 'bg-red-500';
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'stable': return 'text-green-400';
      case 'propagating': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Optics & Waveguide Control</h2>
        <div className="flex space-x-2">
          <Button 
            onClick={runOpticsDiagnostics}
            disabled={isRunning}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isRunning ? 'Running...' : 'Run Diagnostics'}
          </Button>
          <Button 
            onClick={activateProjection}
            disabled={!projectionStatus.ready}
            className={`${projectionStatus.projectionActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
          >
            {projectionStatus.projectionActive ? 'Stop Projection' : 'Activate Projection'}
          </Button>
        </div>
      </div>

      {/* System Status */}
      <Card className="bg-gray-900 border-blue-500/30">
        <CardHeader>
          <CardTitle className="text-blue-300">System Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <Badge className={getStatusColor(projectionStatus.auraValidated)}>
                Aura {projectionStatus.auraValidated ? 'Valid' : 'Invalid'}
              </Badge>
              <p className="text-xs text-gray-400 mt-1">Spiritual Alignment</p>
            </div>
            <div className="text-center">
              <Badge className={getStatusColor(projectionStatus.opticsAligned)}>
                Optics {projectionStatus.opticsAligned ? 'Aligned' : 'Misaligned'}
              </Badge>
              <p className="text-xs text-gray-400 mt-1">Hardware Status</p>
            </div>
            <div className="text-center">
              <Badge className={getStatusColor(projectionStatus.ready)}>
                System {projectionStatus.ready ? 'Ready' : 'Not Ready'}
              </Badge>
              <p className="text-xs text-gray-400 mt-1">Overall Status</p>
            </div>
            <div className="text-center">
              <Badge className={getStatusColor(projectionStatus.projectionActive)}>
                Projection {projectionStatus.projectionActive ? 'Active' : 'Inactive'}
              </Badge>
              <p className="text-xs text-gray-400 mt-1">Output Status</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Waveguide Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-900 border-blue-500/30">
          <CardHeader>
            <CardTitle className="text-blue-300">Waveguide Simulator</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">Input Angle</span>
                <span className="text-white font-mono">{opticsState.waveguideAngle}°</span>
              </div>
              <input
                type="range"
                min="0"
                max="90"
                value={opticsState.waveguideAngle}
                onChange={(e) => adjustWaveguide(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
            
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">Propagation State</span>
                <span className={`font-mono ${getStateColor(opticsState.propagationState)}`}>
                  {opticsState.propagationState.toUpperCase()}
                </span>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">DOE Redirection</span>
                <span className="text-white font-mono">{opticsState.doeRedirection.toFixed(2)}°</span>
              </div>
              <Progress 
                value={(opticsState.doeRedirection / 90) * 100} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-blue-500/30">
          <CardHeader>
            <CardTitle className="text-blue-300">Varifocal Lens Control</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">Lens Adjustment</span>
                <span className="text-white font-mono">{opticsState.lensAdjustment.toFixed(3)}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={opticsState.lensAdjustment}
                onChange={(e) => adjustLens(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">Focal Length</span>
                <span className="text-white font-mono">{opticsState.focalLength.toFixed(2)} mm</span>
              </div>
              <Progress 
                value={(opticsState.focalLength / 40) * 100} 
                className="h-2"
              />
            </div>

            <div className="text-xs text-gray-400">
              Last updated: {new Date(opticsState.timestamp).toLocaleTimeString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Integration Status */}
      {projectionStatus.projectionActive && (
        <Card className="bg-gray-900 border-green-500/30">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-pulse rounded-full h-16 w-16 bg-green-500/20 border-2 border-green-500 mx-auto mb-4 flex items-center justify-center">
                <div className="rounded-full h-8 w-8 bg-green-500"></div>
              </div>
              <p className="text-green-300 font-semibold">NEXUS Projection Active</p>
              <p className="text-sm text-gray-400 mt-2">Aura validated • Optics aligned • Experience protected</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!projectionStatus.ready && (
        <Card className="bg-gray-900 border-yellow-500/30">
          <CardContent className="py-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
              <div>
                <p className="text-yellow-300">System Not Ready</p>
                <p className="text-sm text-gray-400">
                  {!projectionStatus.auraValidated && 'Aura validation required • '}
                  {!projectionStatus.opticsAligned && 'Optics alignment needed'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}